import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { all_routes } from "../../../router/all_routes";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import { Spin } from "antd";

interface DashboardData {
  sales: {
    total_sales: number;
    scts_sales: number;
    ets_sales: number;
  };
  revenue: {
    total_revenue: number;
    scts_revenue: number;
    ets_revenue: number;
  };
}

interface StudentsCardInteface {
  total_students: number;
  ETS_students: number;
  SCTS_students: number;
}

const Cards = () => {
  const { userPermissions, webApi } = useUserPermissions();
  const jwtToken = Cookies.get("authToken");
  const routes = all_routes;
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [studentsData, setStusentsData] = useState<StudentsCardInteface>();
  const [loading, setLoading] = useState(true);
  const [selectedfilter, setSelectedFilter] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  const FILTERS = [
    { label: "Today", value: "today" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
    { label: "Overall", value: "" },
  ];

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  //  Cards Data
  const fetchCardsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${webApi}/sed/get_sale_revenue_cards/?filter=${selectedfilter}`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "token_not_valid") {
          navigate("/academic/sri-chaitanya/crm/landing-page");
        }
        throw new Error(errorData.detail || "Failed to fetch SED cards data");
      }
      setData(data.results);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  //  Students Data
  const fetchStudentsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${webApi}/sed/get_students_cards_data/`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "token_not_valid") {
          navigate("/academic/sri-chaitanya/crm/landing-page");
        }
        throw new Error(errorData.detail || "Failed to fetch SED cards data");
      }
      setStusentsData(data.results);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCardsData();
    fetchStudentsData();
  }, []);

  const formatCurrency = (value: number | undefined) => {
    if (!value) return "₹0";

    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;

    return `₹${value.toFixed(0)}`;
  };

  const originalCurrency = (value: number | undefined) => {
    if (!value) return "₹0";
    return `₹${value.toLocaleString("en-IN")}`;
  };
  
  const handleshowtooltip =() =>{
    setShowTooltip((prev)=>!prev)
  }

  if (loading) return <Spin />;

  return (
    <div className="container ">
      {/* Filter Top Right */}
      <div className="row ">
        <div className="col-12 d-flex justify-content-between">
          <div>
            <h3>Overall Matrix</h3>
          </div>
          <select
            className="form-select w-auto"
            value={selectedfilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            {FILTERS.map((filter) => (
              <option key={filter.value || "overall"} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Cards Row */}
      <div className="row g-4">
        {/* Sales Card */}
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="bg-white rounded p-4 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ fontSize: "45px" }}>🎯</div>
              <div className="text-end">
                <h2 className="fw-bold m-0 text-primary">
                  {data?.sales.total_sales}
                </h2>
                <p className="text-muted m-0">Sales</p>
              </div>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span>
                SCTS : <b>{data?.sales.scts_sales}</b>
              </span>
              <span>
                ETS : <b>{data?.sales.ets_sales}</b>
              </span>
            </div>
          </div>
        </div>

        {/* Revenue Card */}
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="bg-white rounded p-4 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ fontSize: "45px" }}>₹</div>
              <div className="text-end">
                <h2
                  className="fw-bold m-0 text-primary revenue-text"
                  onClick={handleshowtooltip}
                >
                  {formatCurrency(data?.revenue?.total_revenue)}
                </h2>

                {showTooltip && (
                  <div className="currency-tooltip">
                    {originalCurrency(data?.revenue?.total_revenue)}
                  </div>
                )}
                <p className="text-muted m-0">Revenue</p>
              </div>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span>
                SCTS : <b>{formatCurrency(data?.revenue.scts_revenue)}</b>
              </span>
              <span>
                ETS : <b>{formatCurrency(data?.revenue.ets_revenue)}</b>
              </span>
            </div>
          </div>
        </div>

        {/* Students Card */}
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="bg-white rounded p-4 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ fontSize: "45px" }}>🧑‍🎓</div>
              <div className="text-end">
                <h2 className="fw-bold m-0 text-primary">
                  {studentsData?.total_students}
                </h2>
                <p className="text-muted m-0">Students</p>
              </div>
            </div>
            <hr />
            <div className="d-flex justify-content-between">
              <span>
                SCTS : <b>{studentsData?.SCTS_students}</b>
              </span>
              <span>
                ETS : <b>{studentsData?.ETS_students}</b>
              </span>
            </div>
          </div>
        </div>

        {/* Fourth Card Placeholder */}
        {/* <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="bg-white rounded p-4 shadow-sm h-100 d-flex align-items-center justify-content-center">
            Card 4
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Cards;
